//
// Copyright 2024 DXOS.org
//

/**
 * Opaque identifier of a peer participating in automerge-repo sync.
 *
 * Branded `string` so it cannot be accidentally confused with arbitrary
 * strings. Matches the `PeerId` type exported by `@automerge/automerge-repo`.
 */
export type PeerId = string & { __peerId: true };

// ─── Classical automerge-repo sync protocol ───────────────────────────────────
// These types mirror upstream `@automerge/automerge-repo` `Message` variants
// (see `automerge-repo/src/network/messages.ts`). They are the wire protocol
// of the classical automerge-repo sync state machine, not DXOS extensions.
// Carried by `EchoEdgeReplicator` (classical edge wiring) and in-memory test
// adapters; NOT used on the subduction channel except via the
// `SubductionConnectionMessage` byte tunnel.

/**
 * Sent by a peer when joining a connection.
 *
 * @remarks
 * Upstream automerge-repo: first frame after `peer-candidate` to negotiate
 * supported protocol versions. The other side responds with a
 * {@link PeerProtocolMessage} containing the selected version.
 */
export type JoinProtocolMessage = {
  type: 'join';
  targetId?: PeerId;
  senderId: PeerId;
  supportedProtocolVersions: string;
  metadata?: Record<string, unknown>;
};

/**
 * Sent by a peer when leaving a connection.
 *
 * @remarks
 * Upstream automerge-repo: signals graceful disconnect so the remote tears
 * down its sync state for this peer before the underlying transport closes.
 */
export type LeaveProtocolMessage = {
  type: 'leave';
  targetId?: PeerId;
  senderId: PeerId;
  metadata?: Record<string, unknown>;
};

/**
 * Sent in response to a {@link JoinProtocolMessage} confirming the handshake
 * and the selected protocol version.
 *
 * @remarks
 * Upstream automerge-repo: completes the version negotiation handshake.
 */
export type PeerProtocolMessage = {
  type: 'peer';
  senderId: PeerId;
  targetId: PeerId;
  selectedProtocolVersion: string;
  metadata?: Record<string, unknown>;
};

/**
 * Sent by a {@link Repo} to request a document from a peer.
 *
 * @remarks
 * Upstream automerge-repo: identical wire shape to {@link SyncProtocolMessage}
 * except this is the initial sync message used when asking the other peer if
 * it has the document. The peer replies with sync messages or
 * {@link UnavailableProtocolMessage}.
 */
export type RequestProtocolMessage = {
  type: 'request';
  senderId: PeerId;
  targetId: PeerId;
  documentId: string;
  data: Uint8Array;
  metadata?: Record<string, unknown>;
};

/**
 * Automerge sync message for a particular document.
 *
 * @remarks
 * Upstream automerge-repo: carries the binary delta produced by Automerge's
 * sync algorithm (`Automerge.generateSyncMessage`). Symmetric: peers exchange
 * sync messages until both sides have converged.
 */
export type SyncProtocolMessage = {
  type: 'sync';
  senderId: PeerId;
  targetId: PeerId;
  documentId: string;
  data: Uint8Array;
  metadata?: Record<string, unknown>;
};

/**
 * Sent by a {@link Repo} to indicate that it does not have the document and
 * none of its connected peers do either.
 *
 * @remarks
 * Upstream automerge-repo: terminates a {@link RequestProtocolMessage} fan-out
 * when no peer in the cluster can serve the document.
 */
export type UnavailableProtocolMessage = {
  type: 'doc-unavailable';
  senderId: PeerId;
  targetId: PeerId;
  documentId: string;
  metadata?: Record<string, unknown>;
};

/**
 * An ephemeral message gossiped between peers.
 *
 * @remarks
 * Upstream automerge-repo: ephemeral messages are not persisted anywhere.
 * The `data` property can be used by the application as needed. The repo
 * gossips these around.
 *
 * To avoid infinite loops, every message carries (a) a `sessionId` randomly
 * generated by the sender at startup time and (b) a sequence number
 * (`count`). The pair lets receivers discard messages they have already seen.
 */
export type EphemeralProtocolMessage = {
  type: 'ephemeral';
  senderId: PeerId;
  targetId: PeerId;
  count: number;
  sessionId: string;
  documentId: string;
  data: Uint8Array;
  metadata?: Record<string, unknown>;
};

/** Wire literal for {@link ErrorProtocolMessage}. */
export const MESSAGE_TYPE_ERROR = 'error';

/**
 * Out-of-band error reported by a peer.
 *
 * @remarks
 * Upstream automerge-repo: surfaces protocol-level failures (e.g. version
 * mismatch, malformed frame). `senderId` and `targetId` are optional so the
 * frame can be emitted before the handshake completes.
 *
 * The `connectionId` field is a DXOS extension carried on the subduction
 * channel only. Edge → client errors with `connectionId` are restart signals
 * for a specific {@link EdgeSubductionReplicatorConnection} lifetime; the
 * client matches it against its local `_connectionId` and tears down only
 * that connection. The field is optional so the same type can carry
 * upstream-style classical-channel errors that have no connection identity.
 */
export type ErrorProtocolMessage = {
  type: typeof MESSAGE_TYPE_ERROR;
  senderId?: PeerId;
  targetId?: PeerId;
  message: string;
  connectionId?: string;
  metadata?: Record<string, unknown>;
};

// ─── Subduction transport / control protocol ──────────────────────────────────
// Frames produced and consumed by `@automerge/automerge-repo`'s subduction
// transport bridge (`NetworkAdapterTransport`) plus the DXOS-side envelope
// and reconnect marker. Carried by the Edge subduction channel
// (`EdgeService.SUBDUCTION_REPLICATOR`) and the in-memory subduction adapter.

/** Wire literal for {@link SubductionConnectionMessage}. */
export const MESSAGE_TYPE_SUBDUCTION_CONNECTION = 'subduction-connection';

/** Wire literal for {@link SubductionFrameEnvelope}. */
export const MESSAGE_TYPE_SUBDUCTION_FRAME = 'subduction-frame';

/**
 * Inner subduction transport frame emitted by automerge-repo's
 * `NetworkAdapterTransport.#sendFrame`.
 *
 * @remarks
 * Owned by upstream `@automerge/automerge-subduction`: `data` is an opaque
 * byte blob (handshake bytes prefixed with `SUH\0`, or post-handshake
 * sedimentree messages prefixed with `SUM\0`); DXOS code MUST NOT inspect
 * `data`.
 *
 * The literal value `'subduction-connection'` matches the
 * `SUBDUCTION_MESSAGE_TYPE` constant in
 * `@automerge/automerge-repo/src/subduction/network.ts`. Do not redefine the
 * value; if upstream renames it, update {@link MESSAGE_TYPE_SUBDUCTION_CONNECTION}.
 *
 * On the Edge subduction channel this frame never appears top-level — it is
 * always wrapped in a {@link SubductionFrameEnvelope}. In-memory subduction
 * adapters carry it directly.
 */
export type SubductionConnectionMessage = {
  type: typeof MESSAGE_TYPE_SUBDUCTION_CONNECTION;
  senderId: PeerId;
  targetId: PeerId;
  data: Uint8Array;
  metadata?: Record<string, unknown>;
};

/**
 * DXOS-specific wire envelope around a {@link SubductionConnectionMessage}.
 *
 * @remarks
 * Specific to the DXOS edge subduction channel; not used by upstream
 * automerge-repo or in-memory subduction adapters.
 *
 * Carries the originator's `connectionId` (the value of
 * `EdgeSubductionReplicatorConnection._connectionId` on the client, and of
 * the matching `ClientSession` on the edge). The edge uses `connectionId` as
 * the session key and echoes it back on every outbound reply.
 *
 * Frames whose `connectionId` does not match the local connection's UUID are
 * stale (e.g. left over from a previous lifetime after
 * `_onRestartRequested` rotated the UUID) and MUST be dropped on receipt.
 */
export type SubductionFrameEnvelope = {
  type: typeof MESSAGE_TYPE_SUBDUCTION_FRAME;
  connectionId: string;
  subductionFrame: SubductionConnectionMessage;
};

// ─── DXOS collection sync protocol ────────────────────────────────────────────
// DXOS extension on top of the automerge-repo Message type, used by
// `CollectionSynchronizer` to negotiate per-collection sync state. Carried
// by both the classical and subduction channels.

/** Wire literal for {@link CollectionQueryMessage}. */
export const MESSAGE_TYPE_COLLECTION_QUERY = 'collection-query';

/**
 * Request the remote peer's view of a collection.
 *
 * @remarks
 * DXOS-specific. Sent by `CollectionSynchronizer` to ask a peer for the set
 * of document heads it has for a given `collectionId`. The peer replies with
 * a {@link CollectionStateMessage}.
 *
 * If the requester already has a `stateTag` from a previous reply, the peer
 * MAY skip sending the state when its current tag matches (no change since
 * the last query).
 */
export type CollectionQueryMessage = {
  type: typeof MESSAGE_TYPE_COLLECTION_QUERY;
  senderId: PeerId;
  targetId: PeerId;
  collectionId: string;

  /**
   * Identifier of the current state.
   * Remote peer will skip sending the state if it has the same tag.
   */
  stateTag?: string;
  metadata?: Record<string, unknown>;
};

/** Wire literal for {@link CollectionStateMessage}. */
export const MESSAGE_TYPE_COLLECTION_STATE = 'collection-state';

/**
 * Reply to a {@link CollectionQueryMessage} carrying the responder's view of
 * a collection.
 *
 * @remarks
 * DXOS-specific. The `state` payload shape is implementation-defined — today
 * it is a `{ documents: Record<DocumentId, Heads[]> }` map produced by the
 * edge from `Subduction.getAllHeads()`, but the type leaves this open for
 * alternative encodings.
 *
 * `stateTag` lets the requester short-circuit future queries when nothing
 * has changed (see {@link CollectionQueryMessage.stateTag}).
 */
export type CollectionStateMessage = {
  type: typeof MESSAGE_TYPE_COLLECTION_STATE;
  senderId: PeerId;
  targetId: PeerId;
  collectionId: string;

  /**
   *  State representation is implementation-defined.
   */
  state?: unknown;

  /**
   * Identifier of the current state.
   */
  stateTag?: string;
  metadata?: Record<string, unknown>;
};

// ─── Channel-level unions ─────────────────────────────────────────────────────

/**
 * Union of every message that flows through the **classical automerge-repo**
 * sync channel (`EchoEdgeReplicator`, in-memory `TestAdapter`).
 *
 * @remarks
 * Combines the upstream automerge-repo sync messages (join / leave / peer /
 * request / sync / doc-unavailable / ephemeral / error) with DXOS's
 * collection-sync extension (collection-query / collection-state).
 *
 * Does NOT include subduction transport frames or the reconnect marker —
 * those live on a separate channel; see {@link SubductionProtocolMessage}.
 */
export type AutomergeProtocolMessage =
  | JoinProtocolMessage
  | PeerProtocolMessage
  | LeaveProtocolMessage
  | RequestProtocolMessage
  | SyncProtocolMessage
  | UnavailableProtocolMessage
  | EphemeralProtocolMessage
  | ErrorProtocolMessage
  | CollectionQueryMessage
  | CollectionStateMessage;

/**
 * Union of every message that flows through the subduction channel **in
 * process** — i.e. between automerge-repo's `NetworkAdapterTransport` and the
 * DXOS adapter / replicator layer, before wire encoding.
 *
 * @remarks
 * Distinct from {@link AutomergeProtocolMessage}, which describes the classical
 * automerge-repo sync protocol carried by `EchoEdgeReplicator` (mesh sync,
 * in-memory test adapters). The subduction channel multiplexes:
 *
 *   - opaque transport frames ({@link SubductionConnectionMessage}) produced
 *     by `@automerge/automerge-repo/subduction/network.ts`;
 *   - DXOS collection sync messages ({@link CollectionQueryMessage} /
 *     {@link CollectionStateMessage});
 *   - edge-initiated restart signals ({@link ErrorProtocolMessage} carrying
 *     the offending `connectionId`).
 *
 * The {@link SubductionFrameEnvelope} is intentionally NOT a member: it only
 * exists on the wire to attach a `connectionId` to a raw
 * {@link SubductionConnectionMessage}. See {@link SubductionProtocolMessageEnveloped}
 * for the corresponding wire union.
 */
export type SubductionProtocolMessage =
  | CollectionQueryMessage
  | CollectionStateMessage
  | SubductionConnectionMessage
  | ErrorProtocolMessage;

/**
 * Union of every message that flows over the **Edge subduction byte channel
 * wire** (`EdgeService.SUBDUCTION_REPLICATOR`), i.e. the cbor-encoded payload
 * of a `RouterMessage` targeting the subduction service.
 *
 * @remarks
 * The wire counterpart of {@link SubductionProtocolMessage} — identical except
 * that {@link SubductionConnectionMessage} is replaced by
 * {@link SubductionFrameEnvelope}, which wraps it together with the originator's
 * `connectionId` so the peer can drop stale frames from previous connection
 * lifetimes.
 *
 * The wrap/unwrap happens at the
 * {@link EdgeSubductionReplicatorConnection} boundary on the client and the
 * matching `SubductionTransport` boundary on the edge. Other code paths (the
 * in-memory `TestAdapter` pair, in-process subduction adapter) carry
 * {@link SubductionProtocolMessage} directly without ever building an envelope.
 */
export type SubductionProtocolMessageEnveloped =
  | CollectionQueryMessage
  | CollectionStateMessage
  | SubductionFrameEnvelope
  | ErrorProtocolMessage;
